// useWsSubscription — hook layer over WsClient.subscribe().
//
// The `handlerRef` pattern is load-bearing: parents almost always pass
// a freshly-allocated arrow function as the handler, and without the
// ref we'd tear down + re-create the subscription on every parent
// re-render. With the ref, the closure inside useEffect stays stable
// (it always calls `handlerRef.current(msg)`), so the subscription
// installs exactly once per channel change.

import { useEffect, useRef } from 'react'
import { claimPairWsBootstrap } from '../lib/pairWsBootstrap'
import type { Channel, WsMessage } from '../ws/WsClient'
import { useWsOptional } from '../ws/WsContext'

export function useWsSubscription<T = unknown>(
  channel: Channel | null,
  handler: (msg: WsMessage<T>) => void
): void {
  const ws = useWsOptional()
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEffect(() => {
    if (!ws || !channel) return
    const unsubscribe = ws.subscribe<T>(channel, (msg) => handlerRef.current(msg))
    // A direct-load bootstrap may already hold this channel (subscribed at
    // module init, ACK already in hand). Claim it: the bootstrap subscription
    // is released and its buffered pre-mount events replay through the real
    // handler synchronously — before any later live event can arrive, and
    // onto the exact barrier path a live-at-mount event would have taken.
    const bootstrapped = claimPairWsBootstrap(channel)
    if (bootstrapped) {
      for (const msg of bootstrapped) handlerRef.current(msg as WsMessage<T>)
    }
    return unsubscribe
  }, [ws, channel])
}
